import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { useFimFusionRuns } from 'features/fim'
import { useEffect } from 'react'
import useSWRImmutable from 'swr/immutable'

import {
  buildStreamedIndexRow,
  type SubjectIndexRow,
  type SubjectsIndexManifest,
  type SubjectsIndexStreamRow,
} from './subjectsIndex'

/** Above this the index is skipped and the catalog stays in server mode —
 * it would blow the local-memory budget. */
export const SUBJECTS_INDEX_ROW_CEILING = 400_000

const loggedSkips = new Set<string>()

/** Real navigations abort an abandoned stream after this grace period;
 * StrictMode's synchronous fake unmount/remount never reaches it. */
const STREAM_ABANDON_MS = 750

let streamConsumers = 0
let activeStream: { key: string; controller: AbortController } | null = null

export type SubjectsIndexStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'error'
  | 'skipped'

export interface SubjectsIndexState {
  status: SubjectsIndexStatus
  /** The run the rows belong to — consumers must check it before trusting. */
  runId: string | null
  rows: SubjectIndexRow[] | undefined
}

/**
 * The whole-run search index behind instant catalog search, read as ONE
 * streamed NDJSON response and compacted line by line so neither the raw
 * body nor the full summary objects are ever retained.
 *
 * This used to be an offset-partitioned parallel drain over the paged list,
 * which made a single-request job into 39 — each re-counting the run and
 * re-walking every row before its offset (measured: 43s against 3.3s of
 * query time). The partition offsets, the concurrency cap, the
 * short-partition check and the interactive-read yield gate all belonged to
 * that shape and are gone with it.
 *
 * One stream per (site, run) session via the immutable SWR cache; a run
 * change re-keys and re-reads. Failures resolve to `error` — the catalog
 * just stays in server mode.
 */
export function useSubjectsIndex(opts: {
  siteID: string | undefined
  /** Gate on idle-after-first-paint; the read must never precede it. */
  enabled: boolean
  /** The run's total from /stats; null = unknown, don't start. */
  total: number | null
}): SubjectsIndexState {
  const { siteID, enabled, total } = opts
  const { authorization, isAuthenticated } = useGeckoAuth()
  const { FIM_SERVICE_URL } = useRuntimeVariables()
  const { publishedRun } = useFimFusionRuns({ siteID, enabled })
  const runId = publishedRun?.id ?? null

  const skipped = total !== null && total > SUBJECTS_INDEX_ROW_CEILING
  const shouldFetch =
    enabled &&
    isAuthenticated &&
    !!siteID &&
    !!runId &&
    total !== null &&
    !skipped

  // Abort an OUTSTANDING stream when the (site, run) key changes or the
  // surface really goes away. The unmount abort is DEFERRED so React
  // StrictMode's simulated unmount/remount pair never kills a live read.
  useEffect(() => {
    streamConsumers += 1
    return () => {
      streamConsumers -= 1
      window.setTimeout(() => {
        if (streamConsumers === 0) {
          activeStream?.controller.abort()
          activeStream = null
        }
      }, STREAM_ABANDON_MS)
    }
  }, [])

  const { data, error, isLoading } = useSWRImmutable(
    shouldFetch
      ? ['fim-subjects-index', FIM_SERVICE_URL, siteID, runId, total]
      : null,
    async (): Promise<{ runId: string; rows: SubjectIndexRow[] }> => {
      const controller = new AbortController()
      const key = `${siteID}:${runId ?? ''}:${total ?? ''}`
      // A stream for a DIFFERENT (site, run) is stale — cancel it now.
      if (activeStream && activeStream.key !== key) {
        activeStream.controller.abort()
      }
      activeStream = { key, controller }

      const url =
        `${FIM_SERVICE_URL}/api/v1/sites/${encodeURIComponent(siteID as string)}` +
        `/subjects/index?run_id=${encodeURIComponent(runId as string)}`
      const response = await fetch(url, {
        headers: authorization ? { Authorization: authorization } : undefined,
        signal: controller.signal,
      })
      if (!response.ok || !response.body) {
        throw new Error(`subjects index request failed: ${response.status}`)
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      const rows: SubjectIndexRow[] = []
      let manifest: SubjectsIndexManifest | undefined
      let buffer = ''

      const take = (line: string) => {
        if (!line) return
        if (!manifest) {
          manifest = JSON.parse(line) as SubjectsIndexManifest
          return
        }
        rows.push(
          buildStreamedIndexRow(JSON.parse(line) as SubjectsIndexStreamRow)
        )
      }

      for (;;) {
        const { done, value } = await reader.read()
        if (done) break
        buffer += decoder.decode(value, { stream: true })
        let cut = buffer.indexOf('\n')
        while (cut >= 0) {
          take(buffer.slice(0, cut))
          buffer = buffer.slice(cut + 1)
          cut = buffer.indexOf('\n')
        }
      }
      take((buffer + decoder.decode()).trim())

      // A truncated stream is a FAILURE, never a quiet success — a partial
      // index would corrupt instant search, so it falls back to server mode.
      if (!manifest) {
        throw new Error('subjects index stream carried no manifest')
      }
      const expected = (manifest as SubjectsIndexManifest).count
      if (rows.length !== expected) {
        throw new Error(
          `subjects index stream delivered ${rows.length} of ${expected} rows`
        )
      }
      return { runId: runId as string, rows }
    }
  )

  if (skipped) {
    const key = `${siteID}:${runId}:${total}`
    if (!loggedSkips.has(key)) {
      loggedSkips.add(key)
      // eslint-disable-next-line no-console
      console.info(
        `[facility-model] instant-search index skipped: ${total} rows exceeds the ${SUBJECTS_INDEX_ROW_CEILING} ceiling`
      )
    }
    return { status: 'skipped', runId, rows: undefined }
  }
  if (!shouldFetch) return { status: 'idle', runId, rows: undefined }
  if (error) return { status: 'error', runId, rows: undefined }
  if (isLoading || !data) return { status: 'loading', runId, rows: undefined }
  return { status: 'ready', runId: data.runId, rows: data.rows }
}
